import { useEffect, useRef, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import type { CheckoutResponse } from '@wallflower/shared';

/** Stripe.js is loaded once per publishable key, not once per dialog opening. */
const stripeCache = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let cached = stripeCache.get(publishableKey);
  if (!cached) {
    cached = loadStripe(publishableKey);
    stripeCache.set(publishableKey, cached);
  }
  return cached;
}

type Stage =
  | { kind: 'loading' }
  | { kind: 'ready'; checkout: CheckoutResponse }
  | { kind: 'error'; message: string }
  | { kind: 'paid' };

/**
 * The one way this app takes payment, shared by the dashboard, the control
 * panel and the freshly-created-wall page. The checkout is Stripe's embedded
 * form rendered in place - the host never leaves for a Stripe-hosted page -
 * and completion arrives twice: `onComplete` here, and the webhook that moves
 * the wall onto its paid plan. The webhook is the one that counts; this dialog
 * only reports what the form said.
 */
export default function PaymentDialog({
  wallName,
  start,
  onPaid,
  onClose,
}: {
  wallName: string;
  /** Starts the checkout - one call per opening of the dialog. */
  start: () => Promise<CheckoutResponse>;
  onPaid: () => void;
  onClose: () => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  // Held in a ref so the effect below can run on mount alone, without a changing
  // callback prop retriggering it and minting a second checkout.
  const startRef = useRef(start);
  startRef.current = start;
  // The one in-flight checkout, kept across a remount rather than a bare "have
  // I started?" flag: a checkout is money-shaped, so the dialog must mint
  // exactly one, and the result must still land when it arrives.
  const pending = useRef<Promise<CheckoutResponse> | null>(null);

  useEffect(() => {
    // StrictMode mounts, unmounts and remounts in development, and refs survive
    // that. Guarding on the ref alone would let the first run's cleanup discard
    // the reply to the only request ever sent, leaving the dialog on "Opening
    // the checkout…" for good; re-attaching to the same promise cannot.
    pending.current ??= startRef.current();
    let active = true;
    pending.current
      .then((checkout) => {
        if (active) setStage({ kind: 'ready', checkout });
      })
      .catch((err: unknown) => {
        if (active) {
          setStage({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Could not start checkout.',
          });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-dusk-deep/80 p-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/* Stripe's form runs to about a thousand pixels once the card fields are
          in. Centering it outright would push the top of a tall one above the
          scroll container, out of reach; this centers only while it fits. */}
      <div
        className="flex min-h-full items-center justify-center"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Pay for ${wallName}`}
          className="panel w-full max-w-lg p-5"
        >
          <div className="flex items-baseline justify-between gap-4">
            <div className="min-w-0">
              <p className="eyebrow">Full wall</p>
              <h2 className="font-display mt-1 truncate text-xl font-semibold">{wallName}</h2>
            </div>
            <button
              type="button"
              className="btn btn-ghost shrink-0 px-3 py-2 text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>

          {stage.kind === 'loading' ? (
            <p className="mt-6 text-sm text-muted">Opening the checkout…</p>
          ) : null}

          {stage.kind === 'error' ? (
            <p role="alert" className="mt-6 text-sm text-danger">
              {stage.message}
            </p>
          ) : null}

          {stage.kind === 'ready' ? (
            <div className="mt-4">
              <EmbeddedCheckoutProvider
                stripe={stripeFor(stage.checkout.publishableKey)}
                options={{
                  clientSecret: stage.checkout.clientSecret,
                  onComplete: () => setStage({ kind: 'paid' }),
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          ) : null}

          {stage.kind === 'paid' ? (
            <div className="mt-6 grid gap-4">
              <p className="text-sm">
                <span className="text-chalk">Payment received.</span>{' '}
                <span className="text-muted">
                  The wall goes live the moment Stripe confirms it, usually a few seconds.
                </span>
              </p>
              <button type="button" className="btn btn-primary justify-self-start" onClick={onPaid}>
                Done
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
